import type { LoadedApp } from '@/features/page-editor/store/types'
import type { CogoKnowledgeRepository, StoreScopedValidation } from '@cogo/kg-repository'
import type { AppValidationReport } from '@/features/design-system/validator/types'
import { runValidationForApp } from '@/features/design-system/validator/run-validation'
import {
  statusToVerdict,
  useValidationVerdictStore,
} from '@/features/validation/verdict-store'

export interface ValidationSlice {
  validationReport: AppValidationReport | null
  validatorRunning: boolean
  validatorError: string | null
  validatorUnresolvedError: { message: string; failedAt: number; runId: number } | null
  validatorLastRunAt: number | null
  runValidation: (repo: CogoKnowledgeRepository & StoreScopedValidation) => Promise<void>
  clearValidation: () => void
}

type StateWithProject = ValidationSlice & { currentProject: LoadedApp | null }
type Set = (fn: (state: StateWithProject) => void) => void
type Get = () => StateWithProject

export function createValidationSlice(
  set: Set,
  get: Get,
  _api: unknown,
): ValidationSlice {
  // A run requested while another is in flight is queued (not dropped) —
  // otherwise a blocks change landing mid-run leaves the kg-shacl verdict
  // marked stale with no re-check scheduled until the NEXT mutation.
  let queuedRepo: (CogoKnowledgeRepository & StoreScopedValidation) | null = null
  let nextRunId = 0

  return {
    validationReport: null,
    validatorRunning: false,
    validatorError: null,
    validatorUnresolvedError: null,
    validatorLastRunAt: null,

    runValidation: async (repo) => {
      if (get().validatorRunning) {
        queuedRepo = repo
        return
      }
      const startedAt = Date.now()
      const runId = ++nextRunId
      const verdictStore = useValidationVerdictStore.getState()
      verdictStore.markRunning('kg-shacl', 'KG SHACL validation', startedAt)
      set((s) => {
        s.validatorRunning = true
        s.validatorError = null
      })
      try {
        const blocks = get().currentProject?.blocks ?? {}
        const report = await runValidationForApp(repo, blocks)
        set((s) => {
          s.validationReport = report
          s.validatorRunning = false
          s.validatorLastRunAt = Date.now()
          if (
            !s.validatorUnresolvedError ||
            s.validatorUnresolvedError.runId < runId
          ) {
            s.validatorUnresolvedError = null
          }
        })
        const worstRow =
          report.rows.find((row) => row.status === 'destructive') ??
          report.rows.find((row) => row.status === 'warning')
        verdictStore.publish({
          source: 'kg-shacl',
          label: 'KG SHACL validation',
          verdict: statusToVerdict(report.status),
          counts: { pass: report.pass, warn: report.warnings, fail: report.violations },
          worstCheck: worstRow ? worstRow.ruleLabel : null,
          startedAt,
        })
      } catch (err) {
        set((s) => {
          s.validatorRunning = false
          s.validatorError = err instanceof Error ? err.message : String(err)
          s.validatorUnresolvedError = {
            message: err instanceof Error ? err.message : String(err),
            failedAt: Date.now(),
            runId,
          }
        })
        verdictStore.publish({
          source: 'kg-shacl',
          label: 'KG SHACL validation',
          // The engine failed to produce a verdict — report unknown, not fix.
          verdict: 'idle',
          worstCheck: `KG validation error: ${err instanceof Error ? err.message : String(err)}`,
          startedAt,
        })
      }
      const next = queuedRepo
      if (next) {
        queuedRepo = null
        await get().runValidation(next)
      }
    },

    clearValidation: () => {
      set((s) => {
        s.validationReport = null
        s.validatorError = null
        s.validatorUnresolvedError = null
        s.validatorLastRunAt = null
      })
    },
  }
}
